package org.example.project.service

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import org.example.project.dashboard.DashboardFeedRegistry
import org.example.project.manager.LumaticaApiManager

object FeedControlApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadFeedConfig(): Map<String, Map<String, String>> {
        val raw = runCatching { LumaticaApiManager.call("feedcontrol/config") }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return DashboardFeedRegistry.defaultConfig()
        val loaded = runCatching { decodeFeedConfig(raw) }.getOrDefault(emptyMap())
        return mergeWithDefaults(loaded)
    }

    suspend fun saveFeedConfig(config: Map<String, Map<String, String>>): Map<String, Map<String, String>> {
        val raw = runCatching {
            LumaticaApiManager.call("feedcontrol/save-config/${apiPart(configPart(config))}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return config
        val saved = runCatching { decodeFeedConfig(raw) }.getOrDefault(config)
        return mergeWithDefaults(saved)
    }

    fun version(): Long = GlobalManager.jsonTopicVersion("dashboardfeeds.json")

    fun isFeedEnabled(config: Map<String, Map<String, String>>, feedKey: String, node: String): Boolean {
        val configured = config[feedKey]?.get(node)
        return configured?.equals("true", ignoreCase = true)
            ?: (node in (DashboardFeedRegistry.definition(feedKey)?.defaultNodes ?: emptySet()))
    }

    private fun configPart(config: Map<String, Map<String, String>>): String =
        config.entries.joinToString(";") { (feedKey, settings) ->
            "$feedKey:${settings.entries.joinToString(",") { "${it.key}=${it.value}" }}"
        }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

    private fun decodeFeedConfig(raw: String): Map<String, Map<String, String>> {
        val root = json.parseToJsonElement(raw).jsonObject
        return root.mapNotNull { (feedKey, value) ->
            val nodeObject = value as? JsonObject ?: return@mapNotNull null
            feedKey to nodeObject.mapNotNull { (node, enabled) ->
                val text = (enabled as? JsonPrimitive)?.contentOrNull ?: return@mapNotNull null
                node to text
            }.toMap()
        }.toMap()
    }

    private fun mergeWithDefaults(config: Map<String, Map<String, String>>): Map<String, Map<String, String>> =
        DashboardFeedRegistry.defaultConfig().mapValues { (feedKey, defaultNodes) ->
            defaultNodes + config[feedKey].orEmpty()
        }
}
